import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, LogoutOutlined as LogoutIcon } from '@mui/icons-material';
import apiClient from './Api';

function SecurityUsers() {
  // State for users
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyBranches, setCompanyBranches] = useState([]);

  // State for two-factor authentication
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);

  // State for add user dialog
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: '',
    branchId: 'all'
  });
  const [addUserLoading, setAddUserLoading] = useState(false);

  // State for edit user dialog
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserLoading, setEditUserLoading] = useState(false);

  // State for delete confirmation dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);

  // Admin credentials change state
  const [showAdminCredentials, setShowAdminCredentials] = useState(false);
  const [adminCredentials, setAdminCredentials] = useState({
    current_password: '',
    new_username: '',
    new_password: '',
    confirm_password: ''
  });
  const [adminCredentialsLoading, setAdminCredentialsLoading] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch initial data
  useEffect(() => {
    fetchUsers();
    fetchBranches();
    fetchTwoFactorStatus();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const usersData = await apiClient.getUsersWithDetails();
      setUsers(usersData.map(user => ({
        ...user,
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        lastLogin: user.last_login ? new Date(user.last_login).toLocaleString('en-GB') : 'Never',
        branchId: user.branch_id || 'all'
      })));
    } catch (error) {
      showSnackbar('Failed to load users: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await apiClient.getBranches();
      const branches = Array.isArray(response) ? response : response.results || [];
      setCompanyBranches(branches);
    } catch (error) {
      console.error('Failed to load branches:', error);
      showSnackbar('Error loading branches', 'error');
    }
  };

  const fetchTwoFactorStatus = async () => {
    try {
      const status = await apiClient.getTwoFactorStatus();
      setTwoFactorEnabled(status.enabled || false);
    } catch (error) {
      console.error('Failed to load two-factor status:', error);
    }
  };

  // Helper function to get branch name by ID (same as AdminConfigurations)
  const getBranchName = (branchId) => {
    if (branchId === 'all' || !branchId) return 'All Branches';
    const branch = companyBranches.find(b => b.id === branchId);
    return branch ? branch.name : 'Unknown Branch';
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleTwoFactorChange = async (event) => {
    const enabled = event.target.checked;
    setTwoFactorLoading(true);
    
    try {
      if (enabled) {
        await apiClient.enableTwoFactor();
      } else {
        await apiClient.disableTwoFactor();
      }
      setTwoFactorEnabled(enabled);
      showSnackbar(`Two-factor authentication ${enabled ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      showSnackbar('Failed to update two-factor authentication: ' + error.message, 'error');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setNewUser({
      firstName: '',
      lastName: '',
      email: '',
      username: '',
      phone: '',
      password: '',
      confirmPassword: '',
      role: '',
      branchId: 'all'
    });
  };

  const handleNewUserChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({ ...prev, [name]: value }));
  };

  const handleAddUser = async () => {
    const validation = apiClient.validateUserData(newUser);
    if (!validation.isValid) {
      showSnackbar('Please fix the form errors', 'error');
      return;
    }
  
    setAddUserLoading(true);
    try {
      const userData = {
        first_name: newUser.firstName,
        last_name: newUser.lastName,
        email: newUser.email,
        username: newUser.username,
        phone: newUser.phone || '',
        password: newUser.password,
        confirm_password: newUser.confirmPassword,
        role: newUser.role,
        branch_id: newUser.branchId === 'all' ? null : newUser.branchId
      };
  
      console.log('Sending user data:', userData); // Debug log
      await apiClient.createUserWithBranch(userData);
      await fetchUsers();
      handleCloseAddDialog();
      showSnackbar('User created successfully');
    } catch (error) {
      console.error('Full error details:', error);
      showSnackbar('Failed to create user: ' + error.message, 'error');
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleOpenEditDialog = (user) => {
    setEditingUser({
      ...user,
      password: '',
      confirmPassword: ''
    });
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingUser(null);
  };

  const handleEditUserChange = (e) => {
    const { name, value } = e.target;
    setEditingUser(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;

    const validation = apiClient.validateUserData(editingUser, true);
    if (!validation.isValid) {
      showSnackbar('Please fix the form errors', 'error');
      return;
    }

    setEditUserLoading(true);
    try {
      const userData = {
        first_name: editingUser.firstName,
        last_name: editingUser.lastName,
        email: editingUser.email,
        username: editingUser.username,
        phone: editingUser.phone === '-' ? '' : editingUser.phone,
        role: editingUser.role,
        branch_id: editingUser.branchId === 'all' ? null : editingUser.branchId,
        ...(editingUser.password && {
          password: editingUser.password,
          confirm_password: editingUser.confirmPassword
        })
      };

      await apiClient.updateUserWithBranch(editingUser.id, userData);
      await fetchUsers(); // Refresh users list
      handleCloseEditDialog();
      showSnackbar('User updated successfully');
    } catch (error) {
      showSnackbar('Failed to update user: ' + error.message, 'error');
    } finally {
      setEditUserLoading(false);
    }
  };

  const handleOpenDeleteDialog = (user) => {
    if (!apiClient.canDeleteUser(user.role)) {
      showSnackbar('You cannot delete this user', 'error');
      return;
    }
    setUserToDelete(user);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setUserToDelete(null);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    setDeleteUserLoading(true);
    try {
      await apiClient.deleteUser(userToDelete.id);
      await fetchUsers(); // Refresh users list
      handleCloseDeleteDialog();
      showSnackbar('User deleted successfully');
    } catch (error) {
      showSnackbar('Failed to delete user: ' + error.message, 'error');
    } finally {
      setDeleteUserLoading(false);
    }
  };

  const handleToggleAdminCredentials = () => {
    setShowAdminCredentials(!showAdminCredentials);
    if (showAdminCredentials) {
      // Reset form when canceling
      setAdminCredentials({
        current_password: '',
        new_username: '',
        new_password: '',
        confirm_password: ''
      });
    }
  };

  const handleAdminCredentialsChange = (e) => {
    const { name, value } = e.target;
    setAdminCredentials(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdateAdminCredentials = async () => {
    if (!adminCredentials.current_password) {
      showSnackbar('Current password is required', 'error');
      return;
    }

    if (adminCredentials.new_password && 
        adminCredentials.new_password !== adminCredentials.confirm_password) {
      showSnackbar('New passwords do not match', 'error');
      return;
    }

    setAdminCredentialsLoading(true);
    try {
      const updateData = {
        current_password: adminCredentials.current_password,
        ...(adminCredentials.new_username && { new_username: adminCredentials.new_username }),
        ...(adminCredentials.new_password && { new_password: adminCredentials.new_password })
      };

      await apiClient.updateAdminCredentials(updateData);
      handleToggleAdminCredentials();
      showSnackbar('Admin credentials updated successfully');
    } catch (error) {
      showSnackbar('Failed to update credentials: ' + error.message, 'error');
    } finally {
      setAdminCredentialsLoading(false);
    }
  };

  const handleSignOutAllDevices = async () => {
    try {
      await apiClient.signOutAllUserDevices();
      showSnackbar('All devices have been signed out successfully');
    } catch (error) {
      showSnackbar('Failed to sign out all devices: ' + error.message, 'error');
    }
  };

  // Check if add user form is valid
  const isAddUserFormValid = () => {
    const validation = apiClient.validateUserData(newUser);
    return validation.isValid;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Admin Credentials Section */}
      <Paper sx={{ p: 3, borderRadius: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2">
            Admin Credentials
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            size="small"
            onClick={handleToggleAdminCredentials}
          >
            {showAdminCredentials ? 'Cancel' : 'Change'}
          </Button>
        </Box>
        
        {showAdminCredentials ? (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              margin="normal"
              label="New Username (optional)"
              name="new_username"
              value={adminCredentials.new_username}
              onChange={handleAdminCredentialsChange}
            />
            <TextField
              fullWidth
              margin="normal"
              type="password"
              label="Current Password"
              name="current_password"
              value={adminCredentials.current_password}
              onChange={handleAdminCredentialsChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              type="password"
              label="New Password (optional)"
              name="new_password"
              value={adminCredentials.new_password}
              onChange={handleAdminCredentialsChange}
            />
            <TextField
              fullWidth
              margin="normal"
              type="password"
              label="Confirm New Password"
              name="confirm_password"
              value={adminCredentials.confirm_password}
              onChange={handleAdminCredentialsChange}
            />
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpdateAdminCredentials}
                disabled={adminCredentialsLoading}
              >
                {adminCredentialsLoading ? <CircularProgress size={24} /> : 'Update Credentials'}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1">
              Username: {apiClient.getCurrentUser()?.username || 'admin'}
            </Typography>
            <Typography variant="body1">
              Password: **********
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Two-Factor Authentication Section */}
      <Paper sx={{ p: 3, borderRadius: 1 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Two-Factor Authentication
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
          <Switch
            checked={twoFactorEnabled}
            onChange={handleTwoFactorChange}
            disabled={twoFactorLoading}
            inputProps={{ 'aria-label': 'toggle two-factor authentication' }}
          />
          <Typography variant="body1" sx={{ ml: 1 }}>
            Enable Two-Factor Authentication
          </Typography>
          {twoFactorLoading && <CircularProgress size={20} sx={{ ml: 2 }} />}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Add an extra layer of security to your account by requiring a verification code in addition to your password.
        </Typography>
      </Paper>

      {/* Users & Roles Section */}
      <Paper sx={{ p: 3, borderRadius: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2">
            Users & Roles
          </Typography>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleOpenAddDialog}
            disabled={!apiClient.canManageUsers()}
            sx={{ 
              px: 3, 
              py: 1,
              bgcolor: '#1976d2', 
              '&:hover': { bgcolor: '#1565c0' } 
            }}
          >
            + ADD USER
          </Button>
        </Box>
        
        <TableContainer component={Box}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Phone Number</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Branch</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.phone || '-'}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>{getBranchName(user.branchId)}</TableCell>
                  <TableCell>{user.lastLogin}</TableCell>
                  <TableCell>
                    {apiClient.canEditUser(user.role) && (
                      <IconButton 
                        color="primary" 
                        onClick={() => handleOpenEditDialog(user)}
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                        <Typography variant="caption" sx={{ ml: 0.5 }}>
                          EDIT
                        </Typography>
                      </IconButton>
                    )}
                    {apiClient.canDeleteUser(user.role) && (
                      <IconButton 
                        color="error" 
                        onClick={() => handleOpenDeleteDialog(user)}
                        size="small"
                        sx={{ ml: 1 }}
                      >
                        <DeleteIcon fontSize="small" />
                        <Typography variant="caption" color="error" sx={{ ml: 0.5 }}>
                          REMOVE
                        </Typography>
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Session Management Section */}
      <Paper sx={{ p: 3, borderRadius: 1 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Session Management
        </Typography>
        <Button 
          variant="outlined" 
          color="error"
          startIcon={<LogoutIcon />}
          onClick={handleSignOutAllDevices}
          sx={{ mt: 1 }}
        >
          SIGN OUT ALL DEVICES
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This will log out all devices that are currently signed in with your account, including this one.
        </Typography>
      </Paper>

      {/* Add User Dialog */}
      <Dialog open={openAddDialog} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={newUser.firstName}
                onChange={handleNewUserChange}
                helperText="At least one name is required"
              />
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={newUser.lastName}
                onChange={handleNewUserChange}
                helperText="At least one name is required"
              />
            </Box>
            <TextField
              fullWidth
              required
              label="Email Address"
              name="email"
              value={newUser.email}
              onChange={handleNewUserChange}
            />
            <TextField
              fullWidth
              required
              label="Username"
              name="username"
              value={newUser.username}
              onChange={handleNewUserChange}
            />
            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              value={newUser.phone}
              onChange={handleNewUserChange}
            />
            <TextField
              fullWidth
              required
              type="password"
              label="Password"
              name="password"
              value={newUser.password}
              onChange={handleNewUserChange}
            />
            <TextField
              fullWidth
              required
              type="password"
              label="Confirm Password"
              name="confirmPassword"
              value={newUser.confirmPassword}
              onChange={handleNewUserChange}
            />
            <FormControl fullWidth required>
              <InputLabel>Role</InputLabel>
              <Select
                name="role"
                value={newUser.role}
                label="Role"
                onChange={handleNewUserChange}
              >
                <MenuItem value="admin">Admin - Full access to all system features and settings</MenuItem>
                <MenuItem value="secretary">Secretary - Limited access to administration</MenuItem>
                <MenuItem value="officer">Officer - Basic access to system</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Applicable Branch</InputLabel>
              <Select
                name="branchId"
                value={newUser.branchId}
                label="Applicable Branch"
                onChange={handleNewUserChange}
              >
                <MenuItem value="all">All Branches</MenuItem>
                {companyBranches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.name} ({branch.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>CANCEL</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleAddUser}
            disabled={!isAddUserFormValid() || addUserLoading}
          >
            {addUserLoading ? <CircularProgress size={24} /> : 'ADD'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          {editingUser && (
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={editingUser.firstName}
                  onChange={handleEditUserChange}
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={editingUser.lastName}
                  onChange={handleEditUserChange}
                />
              </Box>
              <TextField
                fullWidth
                required
                label="Email Address"
                name="email"
                value={editingUser.email}
                onChange={handleEditUserChange}
              />
              <TextField
                fullWidth
                required
                label="Username"
                name="username"
                value={editingUser.username}
                onChange={handleEditUserChange}
              />
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={editingUser.phone === '-' ? '' : editingUser.phone}
                onChange={handleEditUserChange}
              />
              <Typography variant="caption" color="text.secondary">
                Leave password fields empty to keep current password
              </Typography>
              <TextField
                fullWidth
                type="password"
                label="Password"
                name="password"
                value={editingUser.password}
                onChange={handleEditUserChange}
              />
              <TextField
                fullWidth
                type="password"
                label="Confirm Password"
                name="confirmPassword"
                value={editingUser.confirmPassword}
                onChange={handleEditUserChange}
              />
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  name="role"
                  value={editingUser.role}
                  label="Role"
                  onChange={handleEditUserChange}
                >
                  <MenuItem value="admin">Admin - Full access to all system features and settings</MenuItem>
                  <MenuItem value="secretary">Secretary - Limited access to administration</MenuItem>
                  <MenuItem value="officer">Officer - Basic access to system</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Applicable Branch</InputLabel>
                <Select
                  name="branchId"
                  value={editingUser.branchId || 'all'}
                  label="Applicable Branch"
                  onChange={handleEditUserChange}
                >
                  <MenuItem value="all">All Branches</MenuItem>
                  {companyBranches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.name} ({branch.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>CANCEL</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleUpdateUser}
            disabled={editUserLoading}
          >
            {editUserLoading ? <CircularProgress size={24} /> : 'UPDATE'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove {userToDelete?.firstName} {userToDelete?.lastName} from the system? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>CANCEL</Button>
          <Button 
            onClick={handleDeleteUser} 
            color="error" 
            variant="contained"
            disabled={deleteUserLoading}
          >
            {deleteUserLoading ? <CircularProgress size={24} /> : 'REMOVE'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SecurityUsers;